// CreatorProfile — public profile at /u/<referral_code>.
// Shows the creator's bio, avatar (or initial fallback), and a grid of
// their public silos with tag chips.
//
// Tags are clickable — each routes to /discover?tag=<x> for tag browsing.
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import SiloArt from "@/components/SiloArt";

const hasArt = (silo) => !!silo?.cube?.faces?.center?.image_id;

export default function CreatorProfile() {
  const { code } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!code) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/profile/by-code/${encodeURIComponent(code)}`);
        if (!cancel) setData(r.data);
      } catch (e) {
        if (!cancel) setNotFound(true);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [code]);

  if (loading) return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center text-[#6B6A68]">
      Loading profile…
    </div>
  );

  if (notFound || !data) return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center px-4">
      <div className="max-w-md text-center">
        <h1 className="font-serif-display text-2xl text-[#1C1B1A] mb-2">Profile not found</h1>
        <p className="text-sm text-[#6B6A68] mb-5">This creator handle doesn't exist or has been removed.</p>
        <Link to="/" className="text-sm text-[#C16E5A] hover:underline">Back to My Silo</Link>
      </div>
    </div>
  );

  const c = data.creator;
  const initial = (c.name || c.site_name || "?").trim().charAt(0).toUpperCase();
  const displayName = c.site_name?.trim() || c.name?.trim() || "A creator";

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="creator-profile-page">
      {/* Header bar */}
      <div className="border-b border-[#E5E2DD] bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => nav(-1)} className="text-sm text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1">
            <ArrowLeft size={14} weight="duotone" /> Back
          </button>
          <Link to="/" className="text-sm text-[#1C1B1A] font-serif-display">My Silo</Link>
        </div>
      </div>

      <main className="siloart-copy max-w-5xl mx-auto px-4 py-12">
        {/* Bio block */}
        <section className="flex items-start gap-5 mb-12 flex-wrap">
          <div className="shrink-0">
            {/* Oct 2026 (Randy) — *"my uploaded avatar wins, then my top active
                silo's art."* The backend hands over the first live silo (in his
                own order) that actually has a cover; the initial is the last
                resort, not the default it used to be. */}
            {c.profile_avatar_url ? (
              <img
                src={c.profile_avatar_url}
                alt={`${displayName} avatar`}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="w-24 h-24 rounded-full object-cover border border-[#E5E2DD]"
                data-testid="creator-profile-avatar"
              />
            ) : (data.art_enabled && c.cube) ? (
              <SiloArt
                cube={c.cube}
                enabled
                rounded={false}
                className="w-24 h-24 rounded-full border border-[#E5E2DD]"
                data-testid="creator-profile-avatar-art"
              />
            ) : (
              <div
                className="w-24 h-24 rounded-full bg-[#4A5D4E] text-[#FDFCF8] flex items-center justify-center font-serif-display text-3xl"
                data-testid="creator-profile-avatar-fallback"
              >
                {initial}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#A89360] mb-1.5 flex items-center gap-1.5">
              <Sparkle size={12} weight="duotone" /> Creator
            </div>
            <h1 className="font-serif-display text-4xl text-[#1C1B1A] mb-2" data-testid="creator-profile-name">{displayName}</h1>
            {c.profile_bio && (
              <p className="text-base text-[#3A3A38] leading-relaxed max-w-2xl" data-testid="creator-profile-bio">
                {c.profile_bio}
              </p>
            )}
            {/* Feb 18, 2026 — visible handles + website (only those the
                creator opted IN via their per-handle visibility toggle). */}
            {(Object.keys(c.handles || {}).length > 0 || c.website) && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="creator-profile-handles">
                {c.website && (
                  <a href={c.website} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] px-2.5 py-1 rounded-full border border-[#E5E2DD] text-[#4A5D4E] hover:border-[#4A5D4E] transition"
                    data-testid="creator-profile-website">
                    Website ↗
                  </a>
                )}
                {Object.entries(c.handles || {}).flatMap(([k, vs]) => {
                  const arr = Array.isArray(vs) ? vs : [vs];
                  const urls = {
                    youtube: (h) => `https://youtube.com/@${h}`,
                    twitter: (h) => `https://x.com/${h}`,
                    instagram: (h) => `https://instagram.com/${h}`,
                    tiktok: (h) => `https://tiktok.com/@${h}`,
                    twitch: (h) => `https://twitch.tv/${h}`,
                  };
                  const labels = { youtube: "YouTube", twitter: "X", instagram: "Instagram", tiktok: "TikTok", twitch: "Twitch" };
                  return arr.map((v, i) => (
                    <a key={`${k}-${i}`} href={urls[k](v)} target="_blank" rel="noopener noreferrer"
                      className="text-[11px] px-2.5 py-1 rounded-full border border-[#E5E2DD] text-[#4A5D4E] hover:border-[#4A5D4E] transition"
                      data-testid={`creator-profile-handle-${k}-${i}`}>
                      {labels[k]} @{v} ↗
                    </a>
                  ));
                })}
              </div>
            )}
            <div className="text-xs text-[#6B6A68] mt-3">
              {data.silo_count} {data.silo_count === 1 ? "silo" : "silos"} live
            </div>
          </div>
        </section>

        {/* Silo grid */}
        <section>
          <h2 className="text-[10px] uppercase tracking-[0.22em] text-[#A89360] mb-4">Silos</h2>
          {data.silos.length === 0 ? (
            <div className="text-sm text-[#6B6A68] italic">No public silos yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creator-profile-silo-grid">
              {data.silos.map((s) => (
                <SiloCard key={s.id} silo={s} artEnabled={data.art_enabled} />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function SiloCard({ silo, artEnabled }) {
  return (
    <Link
      to={`/s/${silo.slug}`}
      className="block bg-white border border-[#E5E2DD] hover:border-[#C16E5A] rounded-lg p-5 transition group"
      data-testid={`creator-profile-silo-card-${silo.slug}`}
    >
      {/* Oct 2026 (Randy) — the card he pointed at on the fan portal: the
          silo's own art beside its name, with the category words underneath.
          A silo that set no cover keeps the lettered tile so the row of cards
          still reads as a row. */}
      <div className="flex items-start gap-3 mb-2">
        {artEnabled && hasArt(silo) ? (
          <SiloArt cube={silo.cube} enabled className="w-12 h-12 shrink-0" data-testid={`creator-profile-silo-art-${silo.slug}`} />
        ) : (
          <div
            className="w-12 h-12 shrink-0 rounded-xl bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center font-serif-display text-lg"
            data-testid={`creator-profile-silo-art-fallback-${silo.slug}`}
          >
            {(silo.name || "?").trim().charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-serif-display text-lg text-[#1C1B1A] group-hover:text-[#C16E5A] transition truncate">
            {silo.name}
          </h3>
          {silo.tagline && (
            <p className="text-xs text-[#6B6A68] italic line-clamp-2">{silo.tagline}</p>
          )}
        </div>
      </div>
      {silo.tags && silo.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {silo.tags.slice(0, 4).map((t) => (
            <Link
              key={t}
              to={`/discover?tag=${encodeURIComponent(t)}`}
              onClick={(e) => e.stopPropagation()}
              className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#4A5D4E]/10 text-[#4A5D4E] hover:bg-[#4A5D4E]/20 transition"
              data-testid={`creator-profile-silo-tag-${silo.slug}-${t}`}
            >
              {t}
            </Link>
          ))}
        </div>
      )}
      <div className="text-[11px] text-[#A89360] flex items-center gap-1 mt-2">
        Visit silo <ArrowRight size={11} />
      </div>
    </Link>
  );
}
